import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ServiceManagerService } from '../service-manager.service';
import { HttpMethod } from '../../constants/constant';
import { HttpCustomOptions } from 'src/services/http-interceptor.service';

const MESSAGE_MAX_LENGTH = 250;

// Strips HTML tags so limits/required checks apply to the visible text, not markup.
function stripHtml(value: string): string {
  return (value || '').replace(/<[^>]*>/g, '').trim();
}

function plainTextRequired(control: AbstractControl): ValidationErrors | null {
  return stripHtml(control.value).length ? null : { required: true };
}

function plainTextMaxLength(max: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    const length = stripHtml(control.value).length;
    return length > max ? { maxlength: { requiredLength: max, actualLength: length } } : null;
  };
}

@Component({
  selector: 'app-banners',
  templateUrl: './banners.component.html',
  styleUrls: ['./banners.component.scss']
})
export class BannersComponent implements OnInit {
  bannerList: any[] = [];
  bannerForm!: FormGroup;
  isEditing = false;
  editingBannerId: number | null = null;
  bannerToDelete: any = null;
  messageMaxLength = MESSAGE_MAX_LENGTH;

  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['link'],
      ['clean'],
    ],
  };

  constructor(
    private serviceManager: ServiceManagerService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadBanners();
  }

  initForm(): void {
    this.bannerForm = this.fb.group({
      message: ['', [plainTextRequired, plainTextMaxLength(MESSAGE_MAX_LENGTH)]],
      start_datetime: ['', Validators.required],
      end_datetime: ['', Validators.required],
    });
  }

  get messageLength(): number {
    return stripHtml(this.bannerForm.value.message).length;
  }

  loadBanners(): void {
    this.serviceManager.CallBackendServiceAPI('list-banners', {}).subscribe((data: any) => {
      this.bannerList = data.banners || [];
    });
  }

  openCreateDialog(): void {
    this.isEditing = false;
    this.editingBannerId = null;
    this.bannerForm.reset();
    document.getElementById('bannerDialogTrigger')?.click();
  }

  // "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM" for datetime-local input
  private toInputFormat(dt: string): string {
    return dt ? dt.replace(' ', 'T').substring(0, 16) : '';
  }

  // "YYYY-MM-DDTHH:MM" → "YYYY-MM-DD HH:MM:00" for backend
  private toBackendFormat(dt: string): string {
    if (!dt) return '';
    return dt.replace('T', ' ') + ':00';
  }

  openEditDialog(banner: any): void {
    this.isEditing = true;
    this.editingBannerId = banner.id;
    this.bannerForm.patchValue({
      message: banner.message,
      start_datetime: this.toInputFormat(banner.start_datetime),
      end_datetime: this.toInputFormat(banner.end_datetime),
    });
    document.getElementById('bannerDialogTrigger')?.click();
  }

  saveBanner(): void {
    if (this.bannerForm.invalid) return;

    const body: any = {
      message: this.bannerForm.value.message.trim(),
      start_datetime: this.toBackendFormat(this.bannerForm.value.start_datetime),
      end_datetime: this.toBackendFormat(this.bannerForm.value.end_datetime),
    };

    const customOptions: HttpCustomOptions = {
      showErrorMessage: true,
      showSuccessMessage: true,
      createLoaderAction: true,
    };

    if (this.isEditing) {
      body['banner_id'] = this.editingBannerId;
      this.serviceManager.CallBackendServiceAPI('update-banner', body, HttpMethod.POST, customOptions).subscribe(() => {
        document.getElementById('bannerDialogClose')?.click();
        this.loadBanners();
      });
    } else {
      this.serviceManager.CallBackendServiceAPI('create-banner', body, HttpMethod.POST, customOptions).subscribe(() => {
        document.getElementById('bannerDialogClose')?.click();
        this.loadBanners();
      });
    }
  }

  confirmDelete(banner: any): void {
    this.bannerToDelete = banner;
    document.getElementById('deleteDialogTrigger')?.click();
  }

  deleteBanner(): void {
    if (!this.bannerToDelete) return;

    const customOptions: HttpCustomOptions = {
      showErrorMessage: true,
      showSuccessMessage: true,
      createLoaderAction: true,
    };

    this.serviceManager.CallBackendServiceAPI('delete-banner', { banner_id: this.bannerToDelete.id }, HttpMethod.POST, customOptions).subscribe(() => {
      document.getElementById('deleteDialogClose')?.click();
      this.bannerToDelete = null;
      this.loadBanners();
    });
  }

  statusClass(status: string): string {
    if (status === 'active') return 'badge-active';
    if (status === 'upcoming') return 'badge-upcoming';
    return 'badge-expired';
  }
}
